import React from "react";
import { Download, Share, X, Play } from "lucide-react";
import { onInstallableChange, promptInstall, isStandalone, isIos } from "@/lib/pwa";

const DISMISS_KEY = "eg.pwa.dismissed";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.intelisoft.easygutachter&pcampaignid=web_share";

export const InstallPrompt = () => {
  const [installable, setInstallable] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  const forced = React.useMemo(
    () => new URLSearchParams(window.location.search).get("install") === "1",
    []
  );

  React.useEffect(() => onInstallableChange(setInstallable), []);

  React.useEffect(() => {
    if (isStandalone()) return;
    const dismissed = localStorage.getItem(DISMISS_KEY) === "1";
    if (forced) {
      setVisible(true);
      return;
    }
    if (dismissed) return;
    if (installable || isIos()) setVisible(true);
  }, [installable, forced]);

  // ?install=1 + Chrome-Prompt verfügbar → direkt den nativen Dialog zeigen
  React.useEffect(() => {
    if (forced && installable) {
      promptInstall().then(() => setVisible(false));
    }
  }, [forced, installable]);

  if (!visible || isStandalone()) return null;

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  const handleInstall = async () => {
    const outcome = await promptInstall();
    if (outcome !== "unavailable") setVisible(false);
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[95] w-[calc(100%-24px)] max-w-md bg-white border border-zinc-200 rounded-2xl shadow-xl p-4"
      data-testid="pwa-install-banner"
    >
      <button
        onClick={dismiss}
        className="absolute top-2.5 right-2.5 h-7 w-7 inline-flex items-center justify-center rounded-full text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors"
        aria-label="Schließen"
        data-testid="pwa-install-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3">
        <img src="/icons/icon-192.png" alt="" className="h-11 w-11 rounded-xl border border-zinc-100 shrink-0" />
        <div className="flex-1 min-w-0 pr-5">
          <div className="text-sm font-semibold text-zinc-900">Easy Gutachter installieren</div>
          {isIos() && !installable ? (
            <div className="text-xs text-zinc-500 mt-1 leading-relaxed">
              Tippen Sie auf <Share className="inline h-3.5 w-3.5 mx-0.5 -mt-0.5 text-sky-600" aria-label="Teilen" />
              <b> Teilen</b> und dann auf <b>„Zum Home-Bildschirm"</b> — schon liegt die App auf Ihrem Startbildschirm.
            </div>
          ) : (
            <>
              <div className="text-xs text-zinc-500 mt-1">
                Als App auf dem Startbildschirm — schneller Zugriff, Vollbild, wie eine native App.
              </div>
              <div className="flex items-center gap-2 mt-2.5">
                <button
                  onClick={handleInstall}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white brand-gradient-bg rounded-lg hover:opacity-90 transition-opacity"
                  data-testid="pwa-install-btn"
                >
                  <Download className="h-3.5 w-3.5" /> Jetzt installieren
                </button>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-zinc-700 border border-zinc-300 rounded-lg hover:bg-zinc-50 transition-colors"
                  data-testid="pwa-playstore-btn"
                >
                  <Play className="h-3.5 w-3.5 fill-current" /> Google Play
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
